import React, { useMemo } from "react";
import { RBACProvider, useAuth, type RBACClient } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "./WeBuildClientProvider";

/**
 * RBACProviderWrapper adapts the admin client to the react-auth RBACClient interface.
 *
 * This wrapper:
 * - Gets the authenticated user from AuthUserProvider
 * - Gets the admin client from WeBuildClientProvider
 * - Creates an RBACClient adapter that calls adminClient.rbac.getUserPermissions
 * - Passes the adapter to RBACProvider from react-auth
 *
 * The RBACProvider then fetches permissions and provides them via useRBAC hook.
 *
 * ⚠️ The provider is mounted unconditionally, even before the user and the admin
 * client exist. `useRBAC()` throws outside an `RBACProvider`, and the whole
 * sidebar now reads the RBAC grants through `useCapabilities` — including on the
 * loading page, which `PrivateRoute` renders through `DefaultLayout` while the
 * access token is still being fetched, so `adminClient` is null. Skipping the
 * provider during that window took the app down with
 * "useRBAC must be used within an RBACProvider" before the first paint.
 *
 * `userId` is passed as null until both are ready. `RBACProvider` treats that as
 * "no user", clears its grants and issues no request, then fetches as soon as a
 * real id arrives.
 */
export const RBACProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { adminClient } = useWebuildAPIClient();

  const isReady = Boolean(user?.id && adminClient);

  // Memoised because `RBACProvider` rebuilds its fetch callback whenever this
  // identity changes, so a fresh object per render would re-request the grants.
  const rbacClient = useMemo<RBACClient>(
    () => ({
      getUserPermissions: async ({ userId }) => {
        if (!adminClient) return [];

        return adminClient.rbac.getUserPermissions({ userId });
      },
    }),
    [adminClient],
  );

  return (
    <RBACProvider rbacClient={rbacClient} userId={isReady ? user?.id : null}>
      {children}
    </RBACProvider>
  );
};
